import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'

/**
 * Stimulus controller for managing service image generation with real-time updates
 *
 * Usage:
 *   <div data-controller="admin--service-images"
 *        data-admin--service-images-website-id-value="<%= website.id %>">
 */
export default class extends Controller {
  static values = {
    websiteId: String
  }

  connect() {
    this.generatingServices = new Set()
    this.subscribeToChannel()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  subscribeToChannel() {
    const websiteId = this.websiteIdValue
    if (!websiteId) {
      console.warn('[ServiceImages] No website ID provided')
      return
    }

    this.subscription = consumer.subscriptions.create(
      { channel: 'WebsiteServicesChannel', website_id: websiteId },
      {
        connected: () => {
          console.log('[ServiceImages] Connected to channel')
        },
        disconnected: () => {
          console.log('[ServiceImages] Disconnected from channel')
        },
        received: (data) => {
          console.log('[ServiceImages] Received:', data.event, data)
          this.handleEvent(data)
        }
      }
    )
  }

  handleEvent(data) {
    switch (data.event) {
      case 'service_image_generation_started':
        this.handleGenerationStarted(data)
        break
      case 'service_image_generation_completed':
        this.handleGenerationCompleted(data)
        break
      case 'service_image_generation_failed':
        this.handleGenerationFailed(data)
        break
    }
  }

  // Trigger single service image generation
  async generateImage(event) {
    const btn = event.currentTarget
    const url = btn.dataset.url
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Disable button and show loading state
    btn.disabled = true
    const originalHtml = btn.innerHTML
    btn.innerHTML = `
      <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
      </svg>
    `

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      })

      const data = await response.json()

      if (data.success) {
        // The ActionCable broadcast will handle the UI update
        // Hide the button since generation started
        btn.style.display = 'none'
      } else {
        // Show error and restore button
        btn.disabled = false
        btn.innerHTML = originalHtml
        this.showToast(`Failed to start generation: ${data.error}`, 'error')
      }
    } catch (error) {
      console.error('Image generation request failed:', error)
      btn.disabled = false
      btn.innerHTML = originalHtml
      this.showToast('Failed to start image generation', 'error')
    }
  }

  // Trigger bulk image generation
  async generateAllImages(event) {
    const btn = event.currentTarget
    const url = btn.dataset.url
    const count = btn.dataset.count
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    if (!confirm(`Generate AI images for ${count} services without images?`)) {
      return
    }

    // Disable button and show loading state
    btn.disabled = true
    const originalHtml = btn.innerHTML
    btn.innerHTML = `
      <svg class="w-4 h-4 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
      </svg>
      Generating...
    `

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      })

      const data = await response.json()

      if (data.success) {
        this.showToast(`Started generating images for ${data.count} services`, 'info')
        // Hide the button since all generations started
        btn.style.display = 'none'
      } else {
        btn.disabled = false
        btn.innerHTML = originalHtml
        this.showToast(`Failed: ${data.error}`, 'error')
      }
    } catch (error) {
      console.error('Bulk image generation request failed:', error)
      btn.disabled = false
      btn.innerHTML = originalHtml
      this.showToast('Failed to start bulk generation', 'error')
    }
  }

  handleGenerationStarted(data) {
    this.generatingServices.add(data.service_id)

    const serviceRow = document.getElementById(`service-${data.service_id}`)
    if (serviceRow) {
      // Find or create thumbnail container
      let thumbnailContainer = serviceRow.querySelector('[data-service-thumbnail]')
      if (!thumbnailContainer) {
        thumbnailContainer = this.createThumbnailContainer(serviceRow)
      }

      if (thumbnailContainer) {
        thumbnailContainer.innerHTML = `
          <div class="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg border border-blue-200">
            <svg class="w-5 h-5 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        `
        thumbnailContainer.classList.remove('hidden')
      }

      // Hide the generate button while generating
      const generateBtn = serviceRow.querySelector('.generate-image-btn')
      if (generateBtn) {
        generateBtn.style.display = 'none'
      }
    }

    this.showToast(`Generating image for "${data.service_name}"...`, 'info')
    this.updateBulkGenerateButton()
  }

  handleGenerationCompleted(data) {
    this.generatingServices.delete(data.service_id)

    const serviceRow = document.getElementById(`service-${data.service_id}`)
    if (serviceRow && data.image_url) {
      // Update thumbnail with new image
      let thumbnailContainer = serviceRow.querySelector('[data-service-thumbnail]')
      if (!thumbnailContainer) {
        thumbnailContainer = this.createThumbnailContainer(serviceRow)
      }

      if (thumbnailContainer) {
        thumbnailContainer.innerHTML = `
          <div class="w-12 h-12 rounded-lg overflow-hidden ring-2 ring-green-400 ring-offset-1 transition-all duration-300">
            <img src="${data.image_url}" alt="${data.service_name}" class="w-full h-full object-cover">
          </div>
        `
        thumbnailContainer.classList.remove('hidden')

        // Remove the success ring after animation
        setTimeout(() => {
          const ring = thumbnailContainer.querySelector('.ring-green-400')
          if (ring) {
            ring.classList.remove('ring-2', 'ring-green-400', 'ring-offset-1')
          }
        }, 2000)
      }

      // Remove the generate button since we now have an image
      const generateBtn = serviceRow.querySelector('.generate-image-btn')
      if (generateBtn) {
        generateBtn.remove()
      }

      // Also update the edit form if it's open
      this.updateEditFormImage(data)
    }

    this.showToast(`Image generated for "${data.service_name}"!`, 'success')
    this.updateBulkGenerateButton()
  }

  handleGenerationFailed(data) {
    this.generatingServices.delete(data.service_id)

    const serviceRow = document.getElementById(`service-${data.service_id}`)
    if (serviceRow) {
      // Remove loading indicator
      const thumbnailContainer = serviceRow.querySelector('[data-service-thumbnail]')
      if (thumbnailContainer) {
        thumbnailContainer.innerHTML = `
          <div class="w-12 h-12 flex items-center justify-center bg-red-50 rounded-lg border border-red-200">
            <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
        `

        // Hide after a few seconds
        setTimeout(() => {
          thumbnailContainer.classList.add('hidden')
        }, 3000)
      }

      // Show the generate button again
      const generateBtn = serviceRow.querySelector('.generate-image-btn')
      if (generateBtn) {
        generateBtn.style.display = ''
        generateBtn.disabled = false
      }
    }

    this.showToast(`Failed to generate image for "${data.service_name}": ${data.error}`, 'error')
    this.updateBulkGenerateButton()
  }

  createThumbnailContainer(serviceRow) {
    const dragHandle = serviceRow.querySelector('[data-sortable-handle]')
    if (dragHandle) {
      const container = document.createElement('div')
      container.setAttribute('data-service-thumbnail', '')
      container.className = 'flex-shrink-0'
      dragHandle.after(container)
      return container
    }
    return null
  }

  updateEditFormImage(data) {
    const editForm = document.getElementById(`service-edit-${data.service_id}`)
    if (!editForm) return

    const imageSection = editForm.querySelector('.form-group:has([type="file"])')
    if (!imageSection) return

    const existingImages = imageSection.querySelector('.mb-2.flex')
    if (existingImages) {
      // Add new image to existing images
      const newImageHtml = `
        <div class="relative group">
          <img src="${data.image_url}" class="w-20 h-16 object-cover rounded border border-green-400" alt="${data.service_name}">
          <span class="absolute -top-1 -right-1 px-1.5 py-0.5 bg-green-500 text-white text-xs rounded-full">New</span>
        </div>
      `
      existingImages.insertAdjacentHTML('beforeend', newImageHtml)
    } else {
      // Create images container
      const label = imageSection.querySelector('label')
      if (label) {
        const imagesHtml = `
          <div class="mb-2 flex flex-wrap gap-2">
            <div class="relative group">
              <img src="${data.image_url}" class="w-20 h-16 object-cover rounded border border-green-400" alt="${data.service_name}">
              <span class="absolute -top-1 -right-1 px-1.5 py-0.5 bg-green-500 text-white text-xs rounded-full">New</span>
            </div>
          </div>
        `
        label.insertAdjacentHTML('afterend', imagesHtml)
      }
    }
  }

  updateBulkGenerateButton() {
    const bulkBtn = document.getElementById('bulk-generate-btn')
    if (!bulkBtn) return

    const rows = document.querySelectorAll('[id^="service-"]:not([id*="edit"])')
    let countWithoutImages = 0
    rows.forEach((row) => {
      const hasImage = row.querySelector('[data-service-thumbnail] img') ||
                      row.querySelector('.w-12.h-12 img')
      const isGenerating = row.querySelector('.animate-spin')
      if (!hasImage && !isGenerating) {
        countWithoutImages++
      }
    })

    if (countWithoutImages > 0) {
      bulkBtn.innerHTML = `
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
        </svg>
        Generate Images (${countWithoutImages})
      `
      bulkBtn.style.display = ''
      bulkBtn.dataset.count = countWithoutImages
    } else {
      bulkBtn.style.display = 'none'
    }
  }

  showToast(message, type) {
    // Create toast element
    const toast = document.createElement('div')
    toast.className = `fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg transform transition-all duration-300 translate-x-full flex items-center gap-3 max-w-md`

    const colors = {
      success: 'bg-green-500 text-white',
      error: 'bg-red-500 text-white',
      info: 'bg-blue-500 text-white'
    }
    toast.classList.add(...(colors[type] || colors.info).split(' '))

    const icons = {
      success: '<svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>',
      error: '<svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>',
      info: '<svg class="w-5 h-5 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>'
    }

    toast.innerHTML = `${icons[type] || icons.info}<span>${message}</span>`
    document.body.appendChild(toast)

    // Animate in
    requestAnimationFrame(() => {
      toast.classList.remove('translate-x-full')
    })

    // Remove after delay
    const duration = type === 'info' ? 10000 : 4000
    setTimeout(() => {
      toast.classList.add('translate-x-full', 'opacity-0')
      setTimeout(() => toast.remove(), 300)
    }, duration)
  }
}
